import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { noticeService } from '../services/noticeService';

const NoticeBoard = () => {
    const [notices, setNotices] = useState([]);
    const [analyticsMode, setAnalyticsMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    
    // Auth context / LocalStorage
    const { user } = useSelector((state) => state.auth);
    const token = user?.token;
    const userId = user?._id;
    const userRole = user?.role || (user?.adminName ? 'admin' : 'student');
    
    // Normalize role string for our checks
    const normalizedRole = userRole?.toLowerCase() === 'teacher' ? 'staff' : (userRole?.toLowerCase() || 'student');

    // Create Notice Modal State
    const [showModal, setShowModal] = useState(false);
    const [newNotice, setNewNotice] = useState({
        title: '',
        content: '',
        priority: 'General',
        category: 'General',
        target_audience: 'Students'
    });

    useEffect(() => {
        fetchNotices();
    }, [analyticsMode, normalizedRole]);

    const fetchNotices = async () => {
        if (analyticsMode && (normalizedRole === 'admin' || normalizedRole === 'staff')) {
            const data = await noticeService.getSentNoticesAnalytics(token);
            // Sort by createdAt usually
            setNotices(data || []);
        } else {
            const data = await noticeService.getNotices(token);
            if (data && Array.isArray(data)) {
                // Priority sorting (Urgent first) => then by date
                const priorityWeight = { 'Urgent': 3, 'Important': 2, 'General': 1 };
                data.sort((a, b) => {
                    if (priorityWeight[b.priority] !== priorityWeight[a.priority]) {
                        return priorityWeight[b.priority] - priorityWeight[a.priority];
                    }
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                setNotices(data);
            }
        }
    };

    const handleAcknowledge = async (id) => {
        const res = await noticeService.acknowledgeNotice(id, token);
        if (res.success) {
            // Update local state without refetching
            setNotices(notices.map(n => {
                if(n._id === id) {
                    return { ...n, read_log: [...n.read_log, { user_id: userId }] };
                }
                return n;
            }));
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm('Delete this notice?')) {
            const res = await noticeService.deleteNotice(id, token);
            if(res.success) fetchNotices();
        }
    }

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        const res = await noticeService.createNotice(newNotice, token);
        if (res.success) {
            setShowModal(false);
            setNewNotice({ title: '', content: '', priority: 'General', category: 'General', target_audience: 'Students' });
            fetchNotices();
        } else {
            alert(res.message || 'Error creating notice');
        }
    };

    const filteredNotices = notices.filter(n => {
        const matchSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            n.content.toLowerCase().includes(searchTerm.toLowerCase());
        const matchCat = filterCategory === 'All' || n.category === filterCategory;
        return matchSearch && matchCat;
    });

    const getPriorityStyles = (priority) => {
        switch (priority) {
            case 'Urgent': return 'border-red-500 bg-red-50';
            case 'Important': return 'border-yellow-500 bg-yellow-50';
            default: return 'border-blue-500 bg-white';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'Urgent': return '🚨';
            case 'Important': return '⭐';
            default: return '📄';
        }
    };

    return (
        <div className="p-6 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Circular & Notice Board</h1>
                <div className="flex gap-4">
                    {(normalizedRole === 'admin' || normalizedRole === 'staff') && (
                        <>
                            <button 
                                onClick={() => setAnalyticsMode(!analyticsMode)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 bg-white shadow-sm"
                            >
                                {analyticsMode ? 'View Feed' : 'My Notices (Sent)'}
                            </button>
                            <button 
                                onClick={() => setShowModal(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 shadow-sm"
                            >
                                + Post Notice
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
                <input 
                    type="text" 
                    placeholder="Search keywords..." 
                    className="p-2 border border-gray-300 rounded-md shadow-sm w-full md:w-64 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select 
                    className="p-2 border border-gray-300 rounded-md shadow-sm w-full md:w-48 bg-white focus:ring-blue-500 focus:border-blue-500"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                >
                    <option value="All">All Categories</option>
                    <option value="General">General</option>
                    <option value="Exam">Exam</option>
                    <option value="Holiday">Holiday</option>
                    <option value="Class Update">Class Update</option>
                </select>
            </div>

            {/* Notice Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto">
                {filteredNotices.map(notice => {
                    const isAcknowledged = notice.read_log?.some(l => l.user_id === userId);
                    return (
                        <div key={notice._id} className={`flex flex-col border-l-4 rounded-lg shadow-md p-5 ${getPriorityStyles(notice.priority)}`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="text-xs font-semibold text-gray-500 bg-gray-200/50 px-2 py-1 rounded-full">{notice.category}</div>
                                <span className="text-lg" title={notice.priority}>{getPriorityIcon(notice.priority)}</span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{notice.title}</h3>
                            <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap flex-grow">{notice.content}</p>
                            
                            <div className="text-xs text-gray-500 mb-4">
                                Posted: {new Date(notice.createdAt).toLocaleString()}
                            </div>

                            <div className="mt-auto flex justify-between items-center border-t border-gray-200/50 pt-3">
                                {analyticsMode ? (
                                    <div className="text-sm font-semibold text-blue-700">
                                        Read Count: {notice.stats?.readCount} / {notice.stats?.expectedCount}
                                    </div>
                                ) : (
                                    <button 
                                        disabled={isAcknowledged}
                                        onClick={() => handleAcknowledge(notice._id)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isAcknowledged ? 'bg-green-100 text-green-700 border border-green-200 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        {isAcknowledged ? '✓ Acknowledged' : 'Acknowledge'}
                                    </button>
                                )}
                                
                                {analyticsMode && (
                                    <button onClick={() => handleDelete(notice._id)} className="text-red-500 hover:text-red-700 text-sm">Delete</button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {filteredNotices.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 py-10">No notices found.</div>
                )}
            </div>

            {/* Create Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
                        <h2 className="text-xl font-bold mb-4">Post a Notice</h2>
                        <form onSubmit={handleCreateSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input required type="text" className="w-full border rounded p-2" value={newNotice.title} onChange={e => setNewNotice({...newNotice, title: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                                <textarea required rows="4" className="w-full border rounded p-2" value={newNotice.content} onChange={e => setNewNotice({...newNotice, content: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                    <select className="w-full border rounded p-2" value={newNotice.priority} onChange={e => setNewNotice({...newNotice, priority: e.target.value})}>
                                        <option value="General">General</option>
                                        <option value="Important">Important</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                    <select className="w-full border rounded p-2" value={newNotice.category} onChange={e => setNewNotice({...newNotice, category: e.target.value})}>
                                        <option value="General">General</option>
                                        <option value="Exam">Exam</option>
                                        <option value="Holiday">Holiday</option>
                                        <option value="Class Update">Class Update</option>
                                    </select>
                                </div>
                            </div>
                            {normalizedRole === 'admin' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                                    <select className="w-full border rounded p-2" value={newNotice.target_audience} onChange={e => setNewNotice({...newNotice, target_audience: e.target.value})}>
                                        <option value="Students">Students Only</option>
                                        <option value="Staff">Staff Only</option>
                                        <option value="Both">Everyone (Both)</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">Staff target audiences will automatically sync to their class.</p>
                                </div>
                            )}
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded font-medium text-gray-700">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded font-medium shadow hover:bg-blue-700">Post</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NoticeBoard;
